import type { PayloadAction } from '@reduxjs/toolkit';
import { nanoid } from '@reduxjs/toolkit';
import type { TFormItem, TSortableInput, TSortableItemBase } from 'components/TextBubbleDragAndDrop';
import { CREATE_MODES, ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import type { TFormKeysV4 } from 'types/questions/question.types.v4';
import { type QuestionEditorsState } from './Question.Editor.Slice';
import {
  type TAvailableLanguageCodes,
  isFormTypeMatrix,
  EQuestionType,
  EFormType,
  type TMatrixRow,
  type TMatrixColumn,
  type TFormSubType,
} from '@hubertai/screening-question-types';

export const getEmptyFormByType = (formType: EFormType, language: TAvailableLanguageCodes): TFormSubType => {
  switch (formType) {
    case EFormType.Text:
      return {
        id: nanoid(),
        type: EFormType.Text,
        label: { [language]: 'Label' },
        placeholder: { [language]: 'Placeholder' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Number:
      return {
        id: nanoid(),
        type: EFormType.Number,
        label: { [language]: 'Label' },
        enabled: { [language]: true },
        min: { [language]: 0 },
        max: { [language]: 100 },
        step: { [language]: 1 },
        placeholder: { [language]: 'Placeholder' },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Email:
      return {
        id: nanoid(),
        type: EFormType.Email,
        label: { [language]: 'Label' },
        placeholder: { [language]: 'Placeholder' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Range:
      return {
        id: nanoid(),
        type: EFormType.Range,
        label: { [language]: 'Label' },
        label_left: { [language]: 'Label Left' },
        label_right: { [language]: 'Label Right' },
        unit: { [language]: 'Unit' },
        min: { [language]: 0 },
        max: { [language]: 100 },
        step: { [language]: 1 },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Slider:
      return {
        id: nanoid(),
        type: EFormType.Slider,
        label: { [language]: 'Label' },
        label_left: { [language]: 'Label Left' },
        label_right: { [language]: 'Label Right' },
        unit: { [language]: 'Unit' },
        min: { [language]: 0 },
        max: { [language]: 100 },
        step: { [language]: 1 },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Checkbox:
      return {
        id: nanoid(),
        type: EFormType.Checkbox,
        label: { [language]: 'Label' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Matrix:
      return {
        id: nanoid(),
        type: EFormType.Matrix,
        display_mode: 'progressive',
        label: { [language]: 'Label' },
        help_text: { [language]: 'Help text' },
        rows: [],
        columns: [],
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.File:
      return {
        id: nanoid(),
        type: EFormType.File,
        label: { [language]: 'Label' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    case EFormType.Phone:
      return {
        id: nanoid(),
        type: EFormType.Phone,
        label: { [language]: 'Label' },
        placeholder: { [language]: 'Placeholder' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
    default:
      return {
        id: nanoid(),
        type: EFormType.Text,
        label: { [language]: 'Label' },
        placeholder: { [language]: 'Placeholder' },
        enabled: { [language]: true },
        is_mandatory: { [language]: false },
        hubiverse_property: '',
        candidate_property: '',
      };
  }
};

const FORM_TYPES = Object.values(EFormType);

function isFormItem(item: TSortableItemBase): item is TFormItem {
  return 'type' in item && typeof item.type === 'string' && FORM_TYPES.includes(item.type as EFormType);
}

function areFormItems(items: TSortableItemBase[]): items is TFormItem[] {
  return items.every(isFormItem);
}

interface QuestionFormState {
  forms: TFormSubType[];
  formErrors?: string[];
}

const initialState: QuestionFormState = {
  forms: [],
};
export const formReducer = {
  resetForm: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId] = {
        ...state.byId[questionId],
        ...initialState,
      };
    }
  },

  dragAndDropForm: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      items: TSortableInput;
      containerIds: string[];
      type: EQuestionType;
      mode: ECreateQuestionMode;
    }>,
  ) => {
    const { questionId, containerIds, type, mode, items } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];

    if (![...CREATE_MODES, ECreateQuestionMode.Edit].includes(mode)) {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Aborted - must be a single/multiple select type question.');
      return;
    }

    if (![EQuestionType.Form].includes(type)) {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Aborted - must be a form type question.');
      return;
    }

    if (!containerIds || !containerIds.length || containerIds.length !== 1 || containerIds[0] !== 'forms') {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Aborted - erroneous container.');
      return;
    }

    const formsContainer = items.forms;
    if (!formsContainer) {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Aborted - forms container is missing.');
      return;
    }

    if (!areFormItems(formsContainer.items)) {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Aborted - invalid form items.');
      return;
    }

    question.forms = [...formsContainer.items];
    question.stateModified = true;
  },

  addForm: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formType: EFormType;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, formType, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = getEmptyFormByType(formType, language);
    x.push(form);

    question.forms = [...x];
    question.stateModified = true;
  },

  removeForm: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
    }>,
  ) => {
    const { questionId, formIndex } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    x.splice(formIndex, 1);

    question.forms = [...x];
    question.stateModified = true;
  },

  updateFormHubiverseProperty: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      value: string;
    }>,
  ) => {
    const { questionId, formIndex, value } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = x[formIndex];
    form.hubiverse_property = value;
    form.candidate_property = value;
    x[formIndex] = form;
    question.forms = [...x];
    question.stateModified = true;
  },

  updateFormField: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      field: TFormKeysV4;
      value: any;
      language?: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, formIndex, field, value, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = Object.assign({}, x[formIndex]);
    if (!form) {
      return;
    }

    if (language) {
      // @ts-ignore
      const fieldValue = Object.assign({}, form[field]);
      fieldValue[language] = value;
      // @ts-ignore
      form[field] = fieldValue;
    } else {
      // @ts-ignore
      form[field] = value;
    }

    x[formIndex] = form;
    question.forms = [...x];
    question.stateModified = true;
  },

  addMatrixRowByIndex: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      rowIndex: number;
      questionLanguage: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, formIndex, rowIndex, questionLanguage } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = Object.assign({}, x[formIndex]);

    const newRow: TMatrixRow = {
      id: nanoid(),
      label: {
        [questionLanguage]: '',
      },
      enabled: {
        [questionLanguage]: true,
      },
      value: null,
    };

    if (isFormTypeMatrix(form)) {
      const rows = [...form.rows];
      rows.splice(rowIndex, 0, newRow);
      form.rows = rows;
      x[formIndex] = form;
      question.forms = [...x];
      question.stateModified = true;
    }
  },

  addMatrixColumnByIndex: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      columnIndex: number;
      questionLanguage: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, formIndex, columnIndex, questionLanguage } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = Object.assign({}, x[formIndex]);

    const newColumn: TMatrixColumn = {
      id: nanoid(),
      label: {
        [questionLanguage]: '',
      },
      enabled: {
        [questionLanguage]: true,
      },
      value: null,
    };

    if (isFormTypeMatrix(form)) {
      const columns = [...form.columns];
      columns.splice(columnIndex, 0, newColumn);
      form.columns = columns;
      x[formIndex] = form;
      question.forms = [...x];
      question.stateModified = true;
    }
  },

  removeMatrixRowByIndex: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      rowIndex: number;
    }>,
  ) => {
    const { questionId, formIndex, rowIndex } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = x[formIndex];
    // @ts-ignore
    form.rows.splice(rowIndex, 1);
    x[formIndex] = form;
    question.forms = [...x];
    question.stateModified = true;
  },

  removeMatrixColumnByIndex: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      columnIndex: number;
    }>,
  ) => {
    const { questionId, formIndex, columnIndex } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.forms];
    const form = x[formIndex];
    // @ts-ignore
    form.columns.splice(columnIndex, 1);
    x[formIndex] = form;

    question.forms = [...x];
    question.stateModified = true;
  },

  updateMatrixRowColumnByIndex: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      formIndex: number;
      fieldName: 'rows' | 'columns';
      fieldIndex: number;
      updateFieldName: 'label' | 'value';
      updateFieldValue: string | number;
      questionLanguage: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, formIndex, fieldName, fieldIndex, updateFieldName, updateFieldValue, questionLanguage } =
      action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const forms = [...question.forms];
    const form = { ...forms[formIndex] };

    if (!isFormTypeMatrix(form)) {
      return;
    }

    if (updateFieldName === 'value') {
      const columns = [...form.columns];
      const column = { ...columns[fieldIndex] };
      column.value = updateFieldValue;
      columns[fieldIndex] = column;
      form.columns = columns;
      forms[formIndex] = form;
      question.forms = [...forms];
      question.stateModified = true;
      return;
    }

    if (fieldName === 'rows' && typeof updateFieldValue === 'string') {
      const rows = [...form.rows];
      const row = { ...rows[fieldIndex] };
      const label = { ...row.label };
      label[questionLanguage] = updateFieldValue;
      row.label = label;
      rows[fieldIndex] = row;
      form.rows = rows;
      forms[formIndex] = form;
      question.forms = [...forms];
      question.stateModified = true;
    } else if (fieldName === 'columns' && typeof updateFieldValue === 'string') {
      const columns = [...form.columns];
      const column = { ...columns[fieldIndex] };
      const label = { ...column.label };
      label[questionLanguage] = updateFieldValue;
      column.label = label;
      columns[fieldIndex] = column;
      form.columns = columns;
      forms[formIndex] = form;
      question.forms = [...forms];
      question.stateModified = true;
    } else {
      if (!question.formErrors) question.formErrors = [];
      question.formErrors.push('Error - form not updated');
    }
  },
};
